import { useCallback, useEffect, useRef } from 'react'

import type { CheckoutFlow } from '@/checkout-paths'
import type {
    CheckoutBeginCheckoutTrackingPayload,
    CheckoutBeginPaymentCustomer,
    CheckoutBeginPaymentTrackingPayload,
    CheckoutPurchaseTrackingPayload,
    CheckoutTrackingPayload,
    WindowWithDataLayer,
} from '@/types/checkout-tracking'

export function isCheckoutTrackingEnabled({
    isPwa,
    checkoutFlow,
}: {
    isPwa: boolean
    checkoutFlow: CheckoutFlow
}): boolean {
    return !isPwa && checkoutFlow === 'reservation'
}

export function getCheckoutBeginCheckoutTrackingEvent({
    isPwa,
    checkoutFlow,
    beginCheckoutTracking,
}: {
    isPwa: boolean
    checkoutFlow: CheckoutFlow
    beginCheckoutTracking: CheckoutBeginCheckoutTrackingPayload | null
}): CheckoutBeginCheckoutTrackingPayload | null {
    if (!isCheckoutTrackingEnabled({ isPwa, checkoutFlow })) {
        return null
    }

    return beginCheckoutTracking
}

export function getCheckoutPurchaseTrackingEvent({
    isPwa,
    checkoutFlow,
    paymentStatus,
    purchaseTracking,
}: {
    isPwa: boolean
    checkoutFlow: CheckoutFlow
    paymentStatus: string | null
    purchaseTracking: CheckoutPurchaseTrackingPayload | null
}): CheckoutPurchaseTrackingPayload | null {
    if (
        !isCheckoutTrackingEnabled({ isPwa, checkoutFlow })
        || paymentStatus !== 'successful'
    ) {
        return null
    }

    return purchaseTracking
}

export function buildCheckoutBeginPaymentTrackingEvent(
    beginCheckoutTracking: CheckoutBeginCheckoutTrackingPayload,
    customer: CheckoutBeginPaymentCustomer,
): CheckoutBeginPaymentTrackingPayload {
    return {
        event: 'ec_begin_payment',
        ecommerce: {
            ...beginCheckoutTracking.ecommerce,
            user_name: customer.name,
            user_email: customer.email,
            user_phone_number: customer.phone_number,
            user_phone_number_second: customer.phone_number_2,
        },
        _clear: true,
    }
}

export function getCheckoutTrackingEventKey(event: CheckoutTrackingPayload): string {
    const identifier = 'transaction_id' in event.ecommerce
        ? event.ecommerce.transaction_id
        : event.ecommerce.cart_id

    return `${event.event}:${identifier}`
}

export function pushCheckoutTrackingEvent(
    event: CheckoutTrackingPayload,
    trackedEventKeys: Set<string>,
): void {
    const eventKey = getCheckoutTrackingEventKey(event)

    if (trackedEventKeys.has(eventKey)) {
        return
    }

    const trackingWindow = window as WindowWithDataLayer

    trackingWindow.dataLayer ||= []
    trackingWindow.dataLayer.push(event)
    trackedEventKeys.add(eventKey)
}

export function useCheckoutTracking({
    isPwa,
    checkoutFlow,
    beginCheckoutTracking = null,
    purchaseTracking = null,
    paymentStatus = null,
}: {
    isPwa: boolean
    checkoutFlow: CheckoutFlow
    beginCheckoutTracking?: CheckoutBeginCheckoutTrackingPayload | null
    purchaseTracking?: CheckoutPurchaseTrackingPayload | null
    paymentStatus?: string | null
}): {
    trackBeginPayment: (customer: CheckoutBeginPaymentCustomer) => void
} {
    const trackedEventKeys = useRef(new Set<string>())
    const trackingEnabled = isCheckoutTrackingEnabled({ isPwa, checkoutFlow })
    const beginCheckoutEvent = getCheckoutBeginCheckoutTrackingEvent({
        isPwa,
        checkoutFlow,
        beginCheckoutTracking,
    })
    const purchaseEvent = getCheckoutPurchaseTrackingEvent({
        isPwa,
        checkoutFlow,
        paymentStatus,
        purchaseTracking,
    })

    useEffect(() => {
        if (!beginCheckoutEvent) {
            return
        }

        pushCheckoutTrackingEvent(beginCheckoutEvent, trackedEventKeys.current)
    }, [beginCheckoutEvent])

    useEffect(() => {
        if (!purchaseEvent) {
            return
        }

        pushCheckoutTrackingEvent(purchaseEvent, trackedEventKeys.current)
    }, [purchaseEvent])

    const trackBeginPayment = useCallback((customer: CheckoutBeginPaymentCustomer) => {
        if (!trackingEnabled || !beginCheckoutTracking) {
            return
        }

        pushCheckoutTrackingEvent(
            buildCheckoutBeginPaymentTrackingEvent(beginCheckoutTracking, customer),
            trackedEventKeys.current,
        )
    }, [beginCheckoutTracking, trackingEnabled])

    return { trackBeginPayment }
}
